import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/Motion";
import { buildMeta, buildLinks, breadcrumbSchema } from "@/lib/seo";
import { SITE, whatsappLink } from "@/lib/site";

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: buildMeta({
      title: "Contato — Clínica de Podologia em Belém do Pará | Podomasso",
      description:
        "Entre em contato com a Podomasso, clínica de podologia em Belém do Pará. WhatsApp (91) 8022-5829. Atendimento de domingo a sexta, das 9h às 20h.",
      path: "/contato",
    }),
    links: buildLinks("/contato"),
    scripts: [
      breadcrumbSchema([
        { name: "Início", path: "/" },
        { name: "Contato", path: "/contato" },
      ]),
    ],
  }),
  component: ContatoPage,
});

function ContatoPage() {
  const items = [
    {
      icon: Phone,
      title: "Telefone / WhatsApp",
      content: SITE.phoneDisplay,
      href: `tel:${SITE.phone}`,
    },
    { icon: Mail, title: "E-mail", content: SITE.email, href: `mailto:${SITE.email}` },
    {
      icon: Clock,
      title: "Horário de Funcionamento",
      content: `${SITE.hours.days}: ${SITE.hours.open} às ${SITE.hours.close} • Sábado: Fechado`,
    },
    { icon: MapPin, title: "Endereço", content: "Belém — PA" },
    {
      icon: Instagram,
      title: "Instagram",
      content: "@podomasso",
      href: SITE.social.instagram,
    },
  ];

  return (
    <>
      <section className="bg-gradient-soft py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
              Onde Estamos
            </p>
            <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Localização e <span className="text-gradient-hero">Contato</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
              Venha nos visitar! Estamos prontos para cuidar da saúde dos seus pés em Belém do
              Pará.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <h2 className="font-display text-2xl font-bold sm:text-3xl">
                Informações de Contato
              </h2>
              <ul className="mt-8 space-y-5">
                {items.map((it) => (
                  <li key={it.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                      <it.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{it.title}</p>
                      {it.href ? (
                        <a
                          href={it.href}
                          target={it.href.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className="text-muted-foreground transition-smooth hover:text-primary"
                        >
                          {it.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{it.content}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-8 rounded-2xl bg-secondary/50 p-5 text-sm">
                <strong>Podóloga Responsável:</strong> {SITE.professional.name} —{" "}
                {SITE.professional.registry}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild variant="whatsapp" size="lg">
                  <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-5 w-5" /> Falar pelo WhatsApp
                  </a>
                </Button>
                <Button asChild variant="hero" size="lg">
                  <Link to="/agendamento">Agendar consulta</Link>
                </Button>
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="overflow-hidden rounded-3xl shadow-elegant">
                <iframe
                  title="Mapa Podomasso Belém do Pará"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=-48.55%2C-1.5%2C-48.42%2C-1.4&layer=mapnik&marker=-1.4558%2C-48.4902"
                  className="h-[500px] w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
